import React, { useState } from "react";
import { API_BASE_URL } from "../services/api";

interface ExportPageProps {
  filename: string;
  thread_id: string;
  onBack: () => void;
}

const PLATFORMS = [
  {
    id: "alation",
    name: "Alation",
    icon: "🔵",
    description: "Import metadata in Alation-compatible format",
    color: "#1565c0",
    hasButtons: true,
  },
  {
    id: "informatica", 
    name: "Informatica",
    icon: "🟠",
    description: "Import metadata in Informatica AXON format",
    color: "#e65100",
    hasButtons: true,
  },
  {
    id: "collibra",
    name: "Collibra",
    icon: "🟣",
    description: "Import metadata in Collibra-compatible format",
    color: "#6a1b9a",
    hasButtons: true,
  },
  {
    id: "general",
    name: "Generic JSON",
    icon: "🟢",
    description: "Import in standard JSON format",
    color: "#2e7d32",
    hasButtons: false,
  },
];

const PLATFORM_FORMAT: Record<string, string> = {
  alation: "alation",
  informatica: "informatica",
  collibra: "general",
  general: "general",
};

const ExportPage: React.FC<ExportPageProps> = ({ filename, thread_id, onBack }) => {
  const [loadingStates, setLoadingStates] = useState<Record<string, { push: boolean; download: boolean }>>({});
  const [successStates, setSuccessStates] = useState<Record<string, { push: boolean; download: boolean }>>({});
  const [alationDsId, setAlationDsId] = useState<string>("");

  const setLoading = (platformId: string, action: 'push' | 'download', loading: boolean) => {
    setLoadingStates(prev => ({
      ...prev,
      [platformId]: { ...prev[platformId], [action]: loading }
    }));
  };

  const setSuccess = (platformId: string, action: 'push' | 'download', success: boolean) => {
    setSuccessStates(prev => ({
      ...prev,
      [platformId]: { ...prev[platformId], [action]: success }
    }));
  };

  // Import TO Platform (push data)
  const handleExportToPlatform = async (platformId: string, platformName: string) => {
    if (!thread_id) {
      alert('Session expired. Please go back and re-upload the file.');
      return;
    }
    setLoading(platformId, 'push', true);

    try {
      const ds_id = platformId === 'alation' ? alationDsId.trim() : undefined;
      const response = await fetch(`${API_BASE_URL}/batch/push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ thread_id, target_platform: platformId, ...(ds_id ? { ds_id } : {}) }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${response.status}: Push failed`);
      }

      setSuccess(platformId, 'push', true);
      alert(`Successfully imported to ${platformName} platform!`);

      // Reset success state after 3 seconds
      setTimeout(() => setSuccess(platformId, 'push', false), 3000);

    } catch (error: any) {
      console.error(`Import to ${platformName} failed:`, error);
      alert(`Import to ${platformName} failed: ${error?.message || 'Please try again'}`);
    } finally {
      setLoading(platformId, 'push', false);
    }
  };

  // Download Platform-specific Excel
  const handleDownloadExcel = async (platformId: string, platformName: string) => {
    if (!thread_id) { alert('Session expired. Please go back and re-upload the file.'); return; }
    setLoading(platformId, 'download', true);

    try {
      const output_format = PLATFORM_FORMAT[platformId] || 'general';
      const ds_id = platformId === 'alation' ? alationDsId.trim() : undefined;
      const response = await fetch(`${API_BASE_URL}/batch/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ thread_id, output_format, file_type: 'excel', ...(ds_id ? { ds_id } : {}) }),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const baseName = filename.replace(/\.[^.]+$/, '');
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${baseName}_${platformId}_export.xlsx`;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setSuccess(platformId, 'download', true);
      setTimeout(() => setSuccess(platformId, 'download', false), 3000);

    } catch (error: any) {
      console.error(`Download ${platformName} Excel failed:`, error);
      alert(`Download ${platformName} Excel failed: ${error?.message || 'Unknown error'}`);
    } finally {
      setLoading(platformId, 'download', false);
    }
  };

  // Generic JSON download
  const handleGenericDownload = async () => {
    if (!thread_id) { alert('Session expired. Please go back and re-upload the file.'); return; }
    setLoading('general', 'download', true);

    try {
      const response = await fetch(`${API_BASE_URL}/batch/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ thread_id, output_format: 'general', file_type: 'json' }),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const baseName = filename.replace(/\.[^.]+$/, '');
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${baseName}_generic.json`;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setSuccess('general', 'download', true);
      setTimeout(() => setSuccess('general', 'download', false), 3000);

    } catch (error: any) {
      console.error('Generic JSON download failed:', error);
      alert(`Download failed: ${error?.message || 'Please try again'}`);
    } finally {
      setLoading('general', 'download', false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={onBack} style={styles.backBtn}>← Back</button>
        <h2 style={styles.title}>🗂️ Catalogue in Tool</h2>
      </div>

      {/* File Info */}
      <div style={styles.fileInfoBox}>
        <span style={styles.fileIcon}>📄</span>
        <div>
          <div style={styles.fileLabel}>Source File</div>
          <div style={styles.fileName}>{filename}</div>
        </div>
      </div>

      {/* Alation Datasource ID (required for lineage upload/export) */}
      <div style={styles.dsIdBox}>
        <div style={styles.dsIdLabel}>Alation Datasource ID (ds_id)</div>
        <input
          value={alationDsId}
          onChange={(e) => setAlationDsId(e.target.value)}
          placeholder="e.g. 12345"
          style={styles.dsIdInput}
        />
        <div style={styles.dsIdHelp}>Used for LINEAGE upload + Alation lineage export keys.</div>
      </div>

      {/* Platform Selection */}
      <h3 style={styles.sectionTitle}>Select Data Cataloging Tool:</h3>
      <div style={styles.platformGrid}>
        {PLATFORMS.map((platform) => {
          const loadingState = loadingStates[platform.id] || { push: false, download: false };
          const successState = successStates[platform.id] || { push: false, download: false };
          
          return (
            <div key={platform.id} style={styles.platformCard}>
              <div style={styles.platformIcon}>{platform.icon}</div>
              <div style={{ ...styles.platformName, color: platform.color }}>
                {platform.name}
              </div>
              <div style={styles.platformDesc}>{platform.description}</div>
              
              {/* TWO BUTTONS for Alation, Informatica, Collibra */}
              {platform.hasButtons && (
                <div style={styles.buttonContainer}>
                  {/* Import to Platform Button */}
                  <button
                    style={{
                      ...styles.exportBtn,
                      background: successState.push ? "#4caf50" : platform.color,
                      cursor: loadingState.push ? "not-allowed" : "pointer",
                    }}
                    onClick={() => handleExportToPlatform(platform.id, platform.name)}
                    disabled={loadingState.push}
                  >
                    {loadingState.push ? (
                      "🔄 Importing..."
                    ) : successState.push ? (
                      "✅ Imported"
                    ) : (
                      `🚀 Import to ${platform.name}`
                    )}
                  </button>

                  {/* Download Excel Button */}
                  <button
                    style={{
                      ...styles.downloadBtn,
                      background: successState.download ? "#4caf50" : "#2e7d32",
                      cursor: loadingState.download ? "not-allowed" : "pointer",
                    }}
                    onClick={() => handleDownloadExcel(platform.id, platform.name)}
                    disabled={loadingState.download}
                  >
                    {loadingState.download ? (
                      "📥 Downloading..."
                    ) : successState.download ? (
                      "✅ Downloaded"
                    ) : (
                      `📥 Download ${platform.name} Excel`
                    )}
                  </button>
                </div>
              )}
              
              {/* Single Download button for Generic JSON */}
              {!platform.hasButtons && (
                <button
                  style={{
                    ...styles.genericBtn,
                    background: successState.download ? "#4caf50" : "#2e7d32",
                    cursor: loadingState.download ? "not-allowed" : "pointer",
                  }}
                  onClick={handleGenericDownload}
                  disabled={loadingState.download}
                >
                  {loadingState.download ? "📥 Downloading..." : 
                   successState.download ? "✅ Downloaded" : "📥 Download JSON"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Global Success Message */}
      {Object.values(successStates).some(state => state?.push || state?.download) && (
        <div style={styles.successBox}>
          ✅ Operation completed! Check your downloads folder for files.
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container:         { maxWidth: 900, margin: "0 auto", padding: 32 },
  header:            { display: "flex", alignItems: "center", gap: 16, marginBottom: 24 },
  backBtn:           { padding: "8px 16px", background: "#f0f2f5", border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", fontSize: 14 },
  title:             { fontSize: 24, color: "#333", margin: 0 },
  fileInfoBox:       { display: "flex", alignItems: "center", gap: 16, background: "#fff", border: "1px solid #ddd", borderRadius: 10, padding: 20, marginBottom: 32 },
  fileIcon:          { fontSize: 36 },
  fileLabel:         { fontSize: 12, color: "#999", marginBottom: 4 },
  fileName:          { fontSize: 16, fontWeight: "bold", color: "#333" },
  dsIdBox:           { background: "#fff", border: "1px solid #ddd", borderRadius: 10, padding: 16, marginTop: -20, marginBottom: 32 },
  dsIdLabel:         { fontSize: 12, color: "#999", marginBottom: 8 },
  dsIdInput:         { width: "100%", padding: "10px 12px", borderRadius: 6, border: "1px solid #ccc", fontSize: 14 },
  dsIdHelp:          { marginTop: 8, fontSize: 12, color: "#666" },
  sectionTitle:      { fontSize: 18, color: "#333", marginBottom: 16 },
  platformGrid:      { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 32 },
  platformCard:      { background: "#fff", border: "2px solid #e0e0e0", borderRadius: 12, padding: 24, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", minHeight: 280 },
  platformIcon:      { fontSize: 40, marginBottom: 12 },
  platformName:      { fontSize: 18, fontWeight: "bold", marginBottom: 8 },
  platformDesc:      { fontSize: 13, color: "#666", lineHeight: 1.5, marginBottom: 16, flex: 1 },
  buttonContainer:   { display: "flex", flexDirection: "column", gap: 8, width: "100%" },
  exportBtn:         { padding: "10px 16px", color: "#fff", border: "none", borderRadius: 6, fontSize: 14, fontWeight: "bold", transition: "all 0.2s" },
  downloadBtn:       { padding: "10px 16px", color: "#fff", border: "none", borderRadius: 6, fontSize: 14, fontWeight: "bold", transition: "all 0.2s" },
  genericBtn:        { padding: "10px 16px", color: "#fff", border: "none", borderRadius: 6, fontSize: 14, fontWeight: "bold", marginTop: "auto", transition: "all 0.2s" },
  successBox:        { background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 10, padding: 16, color: "#2e7d32", textAlign: "center", marginTop: 16 },
};

export default ExportPage;